import { useDispatch } from 'react-redux';
import { scenarioActions } from '../scenarios/scenarioSlice';
import { useCallback } from 'react';
import { Scenario, ScenarioComponentType } from '@/types';
import { ArrayElement } from '@/types';

export const useScenarioFunctions = <T extends ScenarioComponentType>(
  componentType: T,
) => {
  const dispatch = useDispatch();

  const removeItem = useCallback(
    (id: string) => {
      dispatch(
        scenarioActions.removeComponent({ componentType, componentId: id }),
      );
    },
    [dispatch, componentType],
  );

  const bulkRemove = useCallback(
    (ids: string[]) => {
      dispatch(
        scenarioActions.bulkDelete({ componentType, componentIds: ids }),
      );
    },
    [dispatch, componentType],
  );

  const restoreItems = useCallback(
    (ids: string[]) => {
      dispatch(
        scenarioActions.restoreRemovedComponents({
          componentType,
          componentIds: ids,
        }),
      );
    },
    [dispatch, componentType],
  );

  const saveItem = useCallback(
    (component: ArrayElement<Scenario[T]>) => {
      dispatch(
        scenarioActions.updateComponent({
          componentType,
          component: component,
        }),
      );
    },
    [dispatch, componentType],
  );

  const append = useCallback(
    (components: Scenario[T]) => {
      dispatch(
        scenarioActions.appendComponents({
          componentType,
          components,
        }),
      );
    },
    [dispatch, componentType],
  );

  const overwrite = useCallback(
    (components: Scenario[T]) => {
      dispatch(
        scenarioActions.overwrite({
          componentType,
          components,
        }),
      );
    },
    [dispatch, componentType],
  );

  const bulkUpdate = useCallback(
    (ids: string[], patch: Partial<ArrayElement<Scenario[T]>>) => {
      dispatch(
        scenarioActions.bulkUpdate({
          componentType,
          componentIds: ids,
          data: patch,
        }),
      );
    },
    [dispatch, componentType],
  );

  return {
    removeItem,
    bulkRemove,
    restoreItems,
    saveItem,
    append,
    overwrite,
    bulkUpdate,
  };
};
